import { AlertTriangle, Info, RotateCcw } from 'lucide-react';
import { clsx } from 'clsx';
import { Button } from '@/shared/ui/Button';
import patterns from '@/shared/ui/styles/patterns.module.css';
import type { AssignmentStatus, EquipmentStatus, PnAction } from '../api/safetyApi';
import styles from './safetyShared.module.css';

/** Deliver / Track / Refer tag — every requirement carries one (PRD §3.2). */
export function PnTag({ action }: { action: PnAction }) {
  const cls =
    action === 'Deliver'
      ? styles.pnDeliver
      : action === 'Track'
        ? styles.pnTrack
        : styles.pnRefer;
  return <span className={cls}>{action}</span>;
}

const ASSIGNMENT_PILL: Record<AssignmentStatus, { label: string; cls: string }> = {
  complete: { label: 'Complete', cls: patterns.pillSuccess },
  pending: { label: 'Scheduled', cls: patterns.pillMuted },
  due_soon: { label: 'Due soon', cls: patterns.pillWarning },
  overdue: { label: 'Overdue', cls: patterns.pillError },
};

export function AssignmentStatusPill({ status }: { status: AssignmentStatus }) {
  const { label, cls } = ASSIGNMENT_PILL[status];
  return <span className={cls}>{label}</span>;
}

const EQUIPMENT_PILL: Record<EquipmentStatus, { label: string; cls: string }> = {
  ok: { label: 'Current', cls: patterns.pillSuccess },
  due_soon: { label: 'Due soon', cls: patterns.pillWarning },
  overdue: { label: 'Overdue', cls: patterns.pillError },
  action_required: { label: 'Action required', cls: patterns.pillError },
  event_driven: { label: 'Per load', cls: patterns.pillMuted },
};

export function EquipmentStatusPill({ status }: { status: EquipmentStatus }) {
  const { label, cls } = EQUIPMENT_PILL[status];
  return <span className={cls}>{label}</span>;
}

export function SeverityDot({ severity }: { severity: 'overdue' | 'due_soon' | 'ok' | 'muted' }) {
  const cls =
    severity === 'overdue'
      ? styles.dotOver
      : severity === 'due_soon'
        ? styles.dotDue
        : severity === 'ok'
          ? styles.dotOk
          : styles.dotMuted;
  return <span className={cls} />;
}

/**
 * Visible failure state for safety pages — a silent blank screen on an API
 * error is indistinguishable from "feature is broken".
 */
export function SafetyLoadError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className={styles.loadError} role="alert">
      <AlertTriangle size={18} aria-hidden className={styles.loadErrorIcon} />
      <div className={styles.loadErrorText}>
        <div className={styles.loadErrorTitle}>Couldn’t load Safety &amp; Training</div>
        <p className={styles.loadErrorDetail}>
          The safety service didn’t respond. This usually clears on retry; if it keeps
          happening, the backend may still be deploying.
        </p>
      </div>
      <Button
        variant="secondary"
        size="sm"
        leftIcon={<RotateCcw size={13} aria-hidden />}
        onClick={onRetry}
      >
        Retry
      </Button>
    </div>
  );
}

/** Fixed honesty copy — rendered verbatim on every Safety view (PRD §3.1). */
export function AttestationFooter({ text, className }: { text: string; className?: string }) {
  return (
    <div className={clsx(styles.attestation, className)}>
      <Info size={13} className={styles.attestationIcon} aria-hidden />
      <p>{text}</p>
    </div>
  );
}
